import type { Maybe, ValueEvent } from '@adornis/base/utilTypes.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { WindowSizeController } from '@adornis/chemistry/controllers/window-size-controller.js';
import { css } from '@adornis/chemistry/directives/css.js';
import '@adornis/chemistry/elements/components/x-flex.js';
import '@adornis/datetime/x-date-picker-dropdown.js';
import '@adornis/forms/x-radiogroup.js';
import '@adornis/popover/x-tooltip.js';
import { ChromiumDialog } from '@chancenwerk/client/design/components/cr-dialog.js';
import { ChromiumSnackbar } from '@chancenwerk/client/design/components/cr-snackbar.js';
import '@chancenwerk/components/chancen-component-card-title.js';
import '@chancenwerk/design/components/cr-button.js';
import '@chancenwerk/design/components/cr-card.js';
import '@chancenwerk/design/components/cr-checkbox.js';
import '@chancenwerk/design/components/cr-date-picker-dropdown.js';
import '@chancenwerk/design/components/cr-dropdown-multiselection.js';
import '@chancenwerk/design/components/cr-dropdown-selection.js';
import '@chancenwerk/design/components/cr-flex.js';
import '@chancenwerk/design/components/cr-grid.js';
import '@chancenwerk/design/components/cr-icon.js';
import '@chancenwerk/design/components/cr-input.js';
import '@chancenwerk/design/components/cr-toggle-button.js';
import '@chancenwerk/design/components/fonts.js';
import { WorkshopAPI } from '@chancenwerk/workshops/api/WorkshopAPI.js';
import { Workshop } from '@chancenwerk/workshops/entities/Workshop.js';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { DateTime } from 'luxon';
import { switchMap } from 'rxjs';

/**
 * @customElement anmeldung-page-registration-workshop-registration
 */
@customElement('anmeldung-page-registration-workshop-registration')
export class AnmeldungPageRegistrationWorkshopRegistration extends ChemistryLitElement {
  size = new WindowSizeController(this);

  @property() workshopID = new RXController(this, '');

  @state() _workshop = new RXController(
    this,
    this.workshopID.observable.pipe(
      switchMap(id =>
        Workshop.subscribeByID<Workshop>(id ?? '')({
          ...Workshop.allFields,
          appointments: {
            start: 1,
            end: 1,
          },
        }),
      ),
    ),
  );
  @state() _done = new RXController(this, false);
  @state() _email = new RXController(this, '');

  private _availableAppointments() {
    return (this._workshop.value?.appointments ?? [])
      .filter(appointment => (appointment.start ?? DateTime.now()).diffNow().milliseconds > 0)
      .toSorted((a, b) => (a.start ?? DateTime.now()).toUnixInteger() - (b.start ?? DateTime.now()).toUnixInteger());
  }

  private _personalData() {
    if (this._availableAppointments().length === 0) {
      return nothing;
    }

    return html` <chancen-component-card-title ${css({ marginTop: this.spacing.xl })}>
        Deine Angaben
      </chancen-component-card-title>
      <cr-text>Gib deine Chancenwerk Email die mit deinem Account verknüpft ist ein:</cr-text>
      <cr-input
        type="email"
        placeholder="E-Mail"
        .value=${this._email.value}
        @value-changed=${(e: ValueEvent<Maybe<string>>) => this._email.next(e.detail.value ?? '')}
      >
      </cr-input>`;
  }

  private _appointments() {
    if (this._availableAppointments().length === 0) {
      return html`<cr-text bold>Es sind aktuell keine Termine verfügbar!</cr-text>`;
    }

    // check that email is a valid email with regex
    if (!this._email.value?.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
      return nothing;
    }

    return html`<chancen-component-card-title ${css({ marginTop: this.spacing.xl })}>
        Termine
      </chancen-component-card-title>
      <cr-text>Wähle einen Termin aus, um dich für den Workshop anzumelden:</cr-text>
      <cr-grid columns="1fr 1fr 1fr" space="md">
        ${repeat(
          this._availableAppointments(),
          appointment => appointment._id,
          appointment => html`<cr-card
            space="sm"
            href="#"
            @click=${async () => {
              if (!(await ChromiumDialog.confirm('Möchtest du dich wirklich für diesen Termin anmelden?'))) {
                return;
              }

              const res = await ChromiumDialog.waitFor<boolean>(
                (async () => {
                  try {
                    return await WorkshopAPI.createRegistrationExternal(this._email.value, appointment._id ?? '')();
                  } catch (e: any) {
                    ChromiumSnackbar.show(e.message);
                  }
                  return false;
                })(),
                'Anmeldung wird erstellt...',
              );

              this._done.next(res);
            }}
          >
            <cr-text>Termin</cr-text>
            <cr-text bold
              >${appointment.start?.toFormat('dd.MM.yyyy HH:mm')} - ${appointment.end?.toFormat('HH:mm')}</cr-text
            >
          </cr-card>`,
        )}
      </cr-grid>`;
  }

  public override render() {
    if (this._done.value) {
      return html`
        <cr-section>
          <cr-card space="md" padding="lg" ${css({ marginTop: this.spacing.xl })}>
            <cr-h1 ${css({ marginBottom: this.spacing.lg })}>Fertig!</cr-h1>
            <cr-text>Falls deine Email zu einem Account gehört, hast du eine Bestätigungsemail erhalten.</cr-text>
          </cr-card>
        </cr-section>
      `;
    }

    return html`
      <cr-section>
        <cr-card space="md" padding="lg" ${css({ marginTop: this.spacing.xl })}>
          <cr-h1 ${css({ marginBottom: this.spacing.lg })}>Anmeldung: ${this._workshop.value?.title}</cr-h1>
          <chancen-component-card-title ${css({ marginTop: this.spacing.xl })}>
            Beschreibung
          </chancen-component-card-title>
          <cr-text>${unsafeHTML(this._workshop.value?.description ?? '')}</cr-text>
          ${this._personalData()} ${this._appointments()}
        </cr-card>
      </cr-section>
    `;
  }
}
